import { useLayoutEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import {
  Home, KeyRound, RefreshCw, Baby, Users, HeartHandshake, ShieldCheck, Wallet,
  Sliders, Coins, UserCheck, TrendingUp, GraduationCap, Briefcase, CalendarClock,
  Repeat, Lock, Landmark, Infinity as InfinityIcon, PiggyBank, Building2, Gift,
  Sparkles, Settings2, Percent, LineChart, ClipboardCheck, Clock, Scale, Target,
  Zap, BookOpen, CalendarCheck, Phone, ArrowUpRight, ChevronRight,
} from "lucide-react";

import { SmoothScroll } from "@/components/SmoothScroll";
import { ScheduleProvider, useSchedule } from "@/components/ScheduleModal";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { getProduct, PRODUCTS } from "@/data/products";
import { businessEntity, orgRef } from "@/data/business";

const SITE = "https://johncampbellinsurance.com";
const PHONE_DISPLAY = "623-414-1766";
const PHONE_HREF = "tel:+16234141766";

const ICONS = {
  Home, KeyRound, RefreshCw, Baby, Users, HeartHandshake, ShieldCheck, Wallet,
  Sliders, Coins, UserCheck, TrendingUp, GraduationCap, Briefcase, CalendarClock,
  Repeat, Lock, Landmark, Infinity: InfinityIcon, PiggyBank, Building2, Gift,
  Sparkles, Settings2, Percent, LineChart, ClipboardCheck, Clock, Scale, Target, Zap,
};
const Icon = ({ name, ...rest }) => {
  const C = ICONS[name] || ShieldCheck;
  return <C {...rest} />;
};

const TRUST = [
  { icon: Scale, title: "Independent Broker", desc: "I work for you, not a single insurer — so my recommendations are built around your needs." },
  { icon: Building2, title: "25+ A-Rated or Better Insurance Carriers", desc: "Access to more than 25 A-rated or better insurance carriers means we compare, not settle." },
  { icon: UserCheck, title: "Personalized Recommendations", desc: "Guidance tailored to your goals, budget, and season of life — never one-size-fits-all." },
  { icon: BookOpen, title: "Education Before Recommendations", desc: "You'll understand your options first, so every decision is truly informed." },
  { icon: HeartHandshake, title: "Faith-Driven Integrity", desc: "Honest, values-led advice you can trust, whether or not you ever buy a thing." },
  { icon: Users, title: "Relationship-First Service", desc: "A real advisor who knows your name and is here long after the paperwork is signed." },
  { icon: RefreshCw, title: "Ongoing Policy Reviews", desc: "Life changes — we revisit your coverage over time to keep it aligned with your goals." },
  { icon: CalendarCheck, title: "No-Cost Consultations", desc: "Clear answers and personalized guidance at no cost and no obligation." },
];

const SectionHeading = ({ kicker, children, className = "" }) => (
  <div className={className}>
    {kicker && <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37] block mb-4">{kicker}</span>}
    <h2 className="serif font-light leading-[1.05] text-[#f4f0ea] text-3xl sm:text-4xl lg:text-5xl">{children}</h2>
  </div>
);

const ProductContent = ({ product }) => {
  const { openSchedule } = useSchedule();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
  }, [product.slug]);

  const related = product.related.map((s) => PRODUCTS[s]).filter(Boolean);

  return (
    <>
      {/* ---------- HERO ---------- */}
      <section data-testid="product-hero" className="relative min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={product.heroImg} alt={product.heroAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/80 to-[#030303]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030303]/85 to-transparent" />
        </div>

        <div className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 lg:pb-24 pt-32">
          {/* breadcrumbs */}
          <nav data-testid="breadcrumbs" aria-label="Breadcrumb" className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-[#737373]">
              <li><Link to="/" className="hover:text-[#d4af37] transition-colors">Home</Link></li>
              <li><ChevronRight size={12} className="text-[#525252]" /></li>
              <li><Link to="/#products" className="hover:text-[#d4af37] transition-colors">Coverages</Link></li>
              <li><ChevronRight size={12} className="text-[#525252]" /></li>
              <li className="text-[#a3a3a3]">{product.name}</li>
            </ol>
          </nav>

          <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#f0d57d] bg-black/50 backdrop-blur-md border border-white/10 rounded-full px-4 py-1.5">{product.category}</span>

          <h1 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-4xl">
            <MaskedLines lines={[product.h1]} />
          </h1>
          <Reveal delay={0.2}>
            <p className="text-[#c9c4bd] leading-relaxed tracking-wide text-lg mt-6 max-w-2xl">{product.intro}</p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button
                onClick={() => openSchedule()}
                data-testid="hero-schedule-cta"
                className="mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#d4af37] text-[#030303] hover:bg-[#e6c860] transition-colors duration-400 rounded-full"
              >
                Schedule Your Complimentary Consultation
              </button>
              <a
                href={PHONE_HREF}
                onClick={(e) => { e.preventDefault(); window.location.href = PHONE_HREF; }}
                data-testid="hero-call-cta"
                className="mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400 rounded-full flex items-center justify-center gap-2"
              >
                <Phone size={14} /> Call John
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <p data-testid="hero-trust" className="mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#a3a3a3] mt-7 flex flex-wrap items-center gap-x-3 gap-y-2">
              <span>Independent Broker</span>
              <span className="text-[#d4af37]">•</span>
              <span>25+ A-Rated or Better Insurance Carriers</span>
              <span className="text-[#d4af37]">•</span>
              <span>No-Cost Consultation</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- OVERVIEW ---------- */}
      <section data-testid="product-overview" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20">
          <Reveal>
            <SectionHeading kicker="Overview">{product.overview.heading}</SectionHeading>
          </Reveal>
          <div className="space-y-6">
            {product.overview.paragraphs.map((p, i) => (
              <Reveal key={p.slice(0, 28)} delay={i * 0.08}>
                <p className="text-[#a3a3a3] leading-relaxed tracking-wide text-lg">{p}</p>
              </Reveal>
            ))}
            {product.localNote && (
              <Reveal>
                <p data-testid="product-local-note" className="text-[#a3a3a3] leading-relaxed tracking-wide text-lg">
                  {product.localNote.before}
                  <Link to="/life-insurance-chandler-az" data-testid="product-chandler-link" className="text-[#d4af37] hover:text-[#f0d57d] link-underline">{product.localNote.anchor}</Link>
                  {product.localNote.after}
                </p>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* ---------- THE CRITICAL PERIOD (optional) ---------- */}
      {product.criticalPeriod && (
        <section data-testid="product-critical-period" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
          <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
            <Reveal className="mb-10">
              <SectionHeading kicker="The Critical Period">{product.criticalPeriod.heading}</SectionHeading>
            </Reveal>
            <div className="space-y-6 border-l-2 border-[#d4af37]/40 pl-8">
              {product.criticalPeriod.paragraphs.map((p, i) => (
                <Reveal key={p.slice(0, 28)} delay={i * 0.08}>
                  <p className="text-[#c9c4bd] leading-relaxed tracking-wide text-lg">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- WHO THIS IS BEST FOR ---------- */}
      <section data-testid="product-bestfor" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <SectionHeading kicker="Who it's for">
              Is <span className="italic text-[#d4af37]">{product.name}</span> right for you?
            </SectionHeading>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {product.bestFor.map((b, i) => (
              <Reveal key={b.label} delay={i * 0.06}>
                <div className="flex items-center gap-4 bg-[#030303] border border-white/10 rounded-sm p-6 hover:border-[#d4af37]/40 transition-colors duration-400 h-full">
                  <span className="shrink-0 h-11 w-11 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                    <Icon name={b.icon} size={18} className="text-[#d4af37]" />
                  </span>
                  <span className="text-[#f4f0ea]/90 tracking-wide">{b.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- KEY BENEFITS ---------- */}
      <section data-testid="product-benefits" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <SectionHeading kicker="Key benefits">What this can mean for your family</SectionHeading>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="group bg-[#0a0a0a] border border-white/10 rounded-sm p-8 h-full hover:border-[#d4af37]/40 transition-colors duration-500">
                  <span className="inline-flex h-12 w-12 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-400">
                    <Icon name={b.icon} size={20} className="text-[#d4af37]" />
                  </span>
                  <h3 className="serif text-2xl text-[#f4f0ea] mb-3">{b.title}</h3>
                  <p className="text-[#a3a3a3] leading-relaxed tracking-wide">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- LIVING BENEFITS (optional) ---------- */}
      {product.livingBenefits && (
        <section data-testid="product-living-benefits" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <Reveal className="mb-10 max-w-3xl">
              <SectionHeading kicker="Living Benefits">{product.livingBenefits.heading}</SectionHeading>
            </Reveal>
            <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16">
              <div className="space-y-6">
                {product.livingBenefits.paragraphs.map((p, i) => (
                  <Reveal key={p.slice(0, 28)} delay={i * 0.06}>
                    <p className="text-[#a3a3a3] leading-relaxed tracking-wide text-lg">{p}</p>
                  </Reveal>
                ))}
                <Reveal>
                  <p className="text-[#f4f0ea]/90 tracking-wide mt-2">Funds accessed through living benefits may generally be used however the policyowner chooses, such as:</p>
                  <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                    {product.livingBenefits.uses.map((u) => (
                      <li key={u} className="flex items-center gap-3 text-[#f4f0ea]/80">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37] shrink-0" />
                        <span className="tracking-wide">{u}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
              <Reveal delay={0.1}>
                <div className="bg-[#030303] border border-white/10 rounded-sm p-8 h-full">
                  <h3 className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mb-6">Good to know</h3>
                  <ul className="space-y-4">
                    {product.livingBenefits.notes.map((n) => (
                      <li key={n.slice(0, 24)} className="flex items-start gap-3 text-[#a3a3a3] text-sm leading-relaxed">
                        <span className="mt-1.5 h-1 w-1 rounded-full bg-[#737373] shrink-0" />
                        {n}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <p className="text-[#c9c4bd] leading-relaxed tracking-wide text-lg mt-10 max-w-3xl border-t border-white/10 pt-8">{product.livingBenefits.closing}</p>
            </Reveal>
          </div>
        </section>
      )}

      {/* ---------- FAQ ---------- */}
      <section data-testid="product-faq" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20">
          <Reveal>
            <SectionHeading kicker="Common questions">
              Questions people<br /><span className="italic text-[#d4af37]">often ask</span>.
            </SectionHeading>
            <p className="text-[#737373] leading-relaxed mt-6 max-w-sm">
              Still wondering about something? A no-cost consultation is the best way to get answers tailored to you.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full">
              {product.faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="serif text-lg sm:text-xl text-[#f4f0ea] hover:no-underline hover:text-[#d4af37] py-6 [&>svg]:text-[#d4af37]"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#a3a3a3] leading-relaxed tracking-wide text-base pb-6 pr-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ---------- WHY JOHN CAMPBELL (reusable trust) ---------- */}
      <section data-testid="product-trust" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <SectionHeading kicker="Why work with John Campbell Insurance">
              An advisor who <span className="italic text-[#d4af37]">educates first</span>.
            </SectionHeading>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            {TRUST.map((t, i) => {
              const T = t.icon;
              return (
                <Reveal key={t.title} delay={(i % 4) * 0.06}>
                  <div className="bg-[#030303] p-8 h-full hover:bg-[#0a0a0a] transition-colors duration-400">
                    <T size={22} className="text-[#d4af37] mb-5" />
                    <h3 className="text-[#f4f0ea] text-lg mb-2 tracking-wide">{t.title}</h3>
                    <p className="text-[#737373] text-sm leading-relaxed">{t.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- RELATED SOLUTIONS ---------- */}
      {related.length > 0 && (
        <section data-testid="product-related" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <Reveal className="mb-12 max-w-3xl">
              <SectionHeading kicker="Related solutions">You may also want to explore</SectionHeading>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.1}>
                  <Link
                    to={`/products/${r.slug}`}
                    data-testid={`related-${r.slug}`}
                    className="group relative flex flex-col h-full bg-[#030303] border border-white/10 rounded-sm overflow-hidden hover:border-[#d4af37]/40 transition-colors duration-500"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img src={r.heroImg} alt={r.heroAlt} loading="lazy" className="w-full h-full object-cover grayscale contrast-125 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/30 to-transparent" />
                    </div>
                    <div className="flex flex-col flex-1 p-7">
                      <div className="flex items-start justify-between">
                        <h3 className="serif text-2xl text-[#f4f0ea] group-hover:text-[#d4af37] transition-colors">{r.name}</h3>
                        <ArrowUpRight className="text-[#737373] group-hover:text-[#d4af37] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-400" size={20} />
                      </div>
                      <span className="mt-6 mono text-xs uppercase tracking-[0.2em] text-[#d4af37]">Learn more →</span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- FINAL CTA ---------- */}
      <section data-testid="product-final-cta" className="relative bg-[#030303] py-20 lg:py-28 border-t border-white/10">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <h2 className="serif font-light leading-[1.05] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
              Protect what <span className="italic text-[#d4af37]">matters most</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#a3a3a3] leading-relaxed tracking-wide text-lg mt-6 max-w-2xl mx-auto">
              Whether you're purchasing coverage for the first time or reviewing an existing policy, I'm here to
              help you understand your options and make informed decisions with confidence.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <button
                onClick={() => openSchedule()}
                data-testid="final-schedule-cta"
                className="mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#d4af37] text-[#030303] hover:bg-[#e6c860] transition-colors duration-400 rounded-full"
              >
                Schedule Consultation
              </button>
              <Link
                to="/#contact"
                data-testid="final-contact-cta"
                className="mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400 rounded-full"
              >
                Contact John
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const product = getProduct(slug);
  if (!product) return <Navigate to="/#products" replace />;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: product.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: product.name,
    name: product.name,
    description: product.intro,
    areaServed: "US",
    provider: orgRef,
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
      { "@type": "ListItem", position: 2, name: "Coverages", item: SITE + "/#products" },
      { "@type": "ListItem", position: 3, name: product.name, item: `${SITE}/products/${product.slug}` },
    ],
  };

  return (
    <div className="App grain bg-[#030303]">
      <Seo
        title={product.seo.title}
        description={product.seo.description}
        path={`/products/${product.slug}`}
        type="article"
        jsonLd={[businessEntity, serviceJsonLd, faqJsonLd, breadcrumbJsonLd]}
      />
      <ScheduleProvider>
        <SmoothScroll>
          <Navbar />
          <main>
            <ProductContent product={product} />
          </main>
          <Footer />
        </SmoothScroll>
      </ScheduleProvider>
    </div>
  );
}
